// src/pages/calendar/categories.ts -- Phase 9 / Cut 1.
//
// The 9 canonical Event categories that come straight out of
// events.dbo.EventTypes. The discovery pass on 2026-05-19 found that the
// prototype's 6-bucket model ("operating / family / gala / charter / santa
// / driver") was design fiction -- there's no Events row that maps to
// "Operating Day", and there's a Festival/Character Event/Visiting Loco
// split that the 6-bucket model collapsed. Chris's call: use the Events
// names verbatim, drop the 6-bucket model.
//
// Naming:
//   * `id` is the Events EventType.name -- e.g. "Family / Activity". Used
//     as the lookup key into CAL_CATEGORIES and as the canonical display
//     label. Do not slug-ify.
//   * `eventTypeId` is the Events EventType.id (stable across renames).
//   * `tone` is one of the 6 design tokens. 9 -> 6 means some categories
//     share a tone; acceptable for V1 (kickoff §The category set).
//   * `short` is the chip label used where there isn't space for the full
//     name -- consistent with the prototype's "FAM" / "OPS" style.
//
// The TONES record below is a local re-creation of the prototype's
// CAL_TONES table (theme.ts only exports raw tokens, no per-tone resolved
// palette). Sourced from theme.ts so a future skin change still cascades
// through to the calendar chips.

import { C } from "../../theme";

export type Tone = "brass" | "heather" | "sage" | "signal" | "paper" | "ink";

/** Tone minus the unrenderable-on-light-surfaces "ink". safeTone returns
 *  this so consumers can't widen back to ink by accident. */
export type RenderTone = Exclude<Tone, "ink">;

export type CalCategory = {
  /** Events EventType.name verbatim -- e.g. "Family / Activity". */
  id: string;
  /** Events EventType.id. */
  eventTypeId: number;
  /** Human-readable label, identical to `id` today but kept separate so a
   *  rename in Events doesn't auto-propagate to the UI (Engineer can ship
   *  a name change without forcing the front-end to redeploy). */
  label: string;
  /** Tone token. Use safeTone() before passing to anything that doesn't
   *  understand "ink" (the AppsPage AppTone union is one such consumer). */
  tone: Tone;
  /** Short chip label -- 3-5 chars uppercase, monospace. */
  short: string;
};

/**
 * The 9 canonical Events categories. Keyed by EventType.name (verbatim).
 * Source of truth: events.dbo.EventTypes (discovered 2026-05-19).
 */
export const CAL_CATEGORIES: Readonly<Record<string, CalCategory>> = {
  "Family / Activity": { id: "Family / Activity", eventTypeId: 1, label: "Family / Activity", tone: "sage",    short: "FAM"   },
  "Festival":          { id: "Festival",          eventTypeId: 2, label: "Festival",          tone: "brass",   short: "FEST"  },
  "Character Event":   { id: "Character Event",   eventTypeId: 3, label: "Character Event",   tone: "brass",   short: "CHAR"  },
  "Evening Event":     { id: "Evening Event",     eventTypeId: 4, label: "Evening Event",     tone: "heather", short: "EVE"   },
  "Gala":              { id: "Gala",              eventTypeId: 5, label: "Gala",              tone: "signal",  short: "GALA"  },
  "Weekend Event":     { id: "Weekend Event",     eventTypeId: 6, label: "Weekend Event",     tone: "sage",    short: "WKND"  },
  "Visiting Loco":     { id: "Visiting Loco",     eventTypeId: 7, label: "Visiting Loco",     tone: "paper",   short: "LOCO"  },
  "Christmas":         { id: "Christmas",         eventTypeId: 8, label: "Christmas",         tone: "signal",  short: "XMAS"  },
  "Learning":          { id: "Learning",          eventTypeId: 9, label: "Learning",          tone: "ink",     short: "LRN"   },
};

/** All 9 categories in canonical order. */
export const CAL_CATEGORY_LIST: ReadonlyArray<CalCategory> = Object.values(CAL_CATEGORIES);

/**
 * Map "ink" -> "brass" so the Avatar / Tile / chip primitives that only
 * know the 5-tone palette still resolve to something visible. Used wherever
 * the prototype had an inline `cat.tone === "ink" ? "brass" : cat.tone`.
 * Returns RenderTone (Tone minus "ink") so consumers can't accidentally
 * widen back to a value that wouldn't render on a light surface.
 */
export function safeTone(t: Tone): RenderTone {
  return t === "ink" ? "brass" : t;
}

/**
 * Resolved tone palette -- bg / fg / border / solid backgrounds for the 6
 * tone tokens. Mirrors the prototype's CAL_TONES structure so the lift
 * preserved the visual contract 1:1. Future-proofed: when src/theme.ts
 * exposes a top-level TONES export, this can be deleted in favour of that.
 */
export type ToneResolved = {
  bg: string;
  fg: string;
  bd: string;
  solid: string;
  solidFg: string;
  soft: string;
  stripe: string;
};

export const TONES: Readonly<Record<Tone, ToneResolved>> = {
  brass:   { bg: C.brassBg,   fg: C.brassDk,   bd: "rgba(167,117,28,0.20)", solid: C.brassDk,  solidFg: C.brassGlow, soft: C.brassSurf,  stripe: C.brassLt },
  heather: { bg: C.heatherBg, fg: C.heatherDk, bd: "rgba(122,93,140,0.22)", solid: C.heather,  solidFg: "#FFFCEC",   soft: "#F4ECF7",     stripe: C.heatherLt },
  sage:    { bg: C.sageBg,    fg: C.sageDk,    bd: "rgba(110,140,114,0.22)",solid: C.sage,     solidFg: "#FFFCEC",   soft: "#EEF3EA",     stripe: C.sageLt },
  signal:  { bg: C.signalBg,  fg: C.signalDk,  bd: "rgba(184,58,43,0.22)",  solid: C.signalDk, solidFg: "#FFFCEC",   soft: "#FBE9E4",     stripe: "#D9A99F" },
  paper:   { bg: C.paperWarm, fg: C.inkSoft,   bd: C.hairline,              solid: C.inkSoft,  solidFg: C.paper,     soft: C.paperWarm,   stripe: C.hairline },
  ink:     { bg: "#332618",   fg: C.brassGlow, bd: "transparent",           solid: C.ink,      solidFg: C.brassGlow, soft: "#3A2C1C",     stripe: C.inkSoft },
};

/** Convenience: resolve `cat.tone` after applying the safeTone fallback. */
export function resolvedTone(cat: CalCategory): ToneResolved {
  return TONES[safeTone(cat.tone)];
}
